import { useState, useEffect } from 'react';

// Shared marketing nav. Used on the public landing surface. The project has no
// router — links are plain anchors that trigger full-page navigation, matching
// the existing public pages. "How it Works" / "Pricing" point at /#how and
// /#pricing so they work from any page (those sections land in P-landing-2).
const NAV_LINKS = [
  { label: 'How it Works', href: '/#how' },
  { label: 'Pricing',      href: '/#pricing' },
  { label: 'Affiliate',    href: '/affiliate' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open,     setOpen]     = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="site-hdr" data-scrolled={scrolled ? 'true' : 'false'}>
      <div className="site-hdr-inner">
        <a href="/" className="site-hdr-brand" aria-label="AdaptOS home">
          <img src="/logo.jpg" alt="AdaptOS" />
        </a>

        <nav className="site-hdr-nav" aria-label="Primary">
          {NAV_LINKS.map(l => (
            <a key={l.href} href={l.href}>{l.label}</a>
          ))}
        </nav>

        <div className="site-hdr-actions">
          <a href="/signin" className="site-hdr-signin">Sign In</a>
          <a href="/registration" className="site-hdr-cta">
            <span className="full">Create Account</span>
            <span className="short">Create</span>
          </a>
          <button
            type="button"
            className="site-hdr-burger"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
          >
            {open ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {open && (
        <nav className="site-hdr-mobile" aria-label="Mobile">
          {NAV_LINKS.map(l => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)}>{l.label}</a>
          ))}
        </nav>
      )}

      <style>{`
        .site-hdr {
          position: sticky; top: 0; z-index: 100; width: 100%;
          background: #1a1a24;
          backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
          border-bottom: 1px solid transparent;
          transition: border-color .25s ease, box-shadow .25s ease, background .25s ease;
        }
        .site-hdr[data-scrolled="true"] {
          border-bottom-color: rgba(255,255,255,0.10);
          box-shadow: 0 6px 28px rgba(0,0,0,0.40);
          background: #16161e;
        }
        .site-hdr-inner {
          max-width: 1200px; margin: 0 auto; height: 132px;
          padding: 0 clamp(16px, 4vw, 40px);
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
        }
        .site-hdr-brand { display: flex; align-items: center; flex-shrink: 0; }
        .site-hdr-brand img { height: 108px; width: auto; object-fit: contain; display: block; }
        .site-hdr-nav { display: flex; align-items: center; gap: 30px; }
        .site-hdr-nav a {
          color: rgba(255,255,255,0.78); text-decoration: none;
          font-size: 15px; font-weight: 500; letter-spacing: .01em;
          transition: color .18s ease;
        }
        .site-hdr-nav a:hover { color: #d97706; }
        .site-hdr-actions { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
        .site-hdr-signin {
          color: rgba(255,255,255,0.85); text-decoration: none;
          font-size: 15px; font-weight: 500; transition: color .18s ease;
        }
        .site-hdr-signin:hover { color: #fff; }
        .site-hdr-cta {
          display: inline-flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff; text-decoration: none;
          font-size: 15px; font-weight: 700; letter-spacing: .01em;
          padding: 10px 20px; border-radius: 10px; white-space: nowrap;
          box-shadow: 0 4px 16px rgba(217,119,6,0.25);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .site-hdr-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(217,119,6,0.38); }
        .site-hdr-cta .short { display: none; }
        .site-hdr-burger {
          display: none; background: none; border: none; cursor: pointer;
          color: #ffffff; font-size: 22px; line-height: 1; padding: 4px 6px;
        }
        .site-hdr-mobile {
          display: flex; flex-direction: column;
          padding: 8px clamp(16px,4vw,40px) 18px;
          border-top: 1px solid rgba(255,255,255,0.08);
          background: #1a1a24;
        }
        .site-hdr-mobile a {
          color: rgba(255,255,255,0.85); text-decoration: none;
          font-size: 16px; font-weight: 500; padding: 12px 0;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .site-hdr-mobile a:last-child { border-bottom: none; }
        @media (max-width: 860px) {
          .site-hdr-nav { display: none; }
          .site-hdr-burger { display: inline-flex; align-items: center; }
        }
        @media (max-width: 600px) {
          .site-hdr-inner { height: 110px; }
          .site-hdr-brand img { height: 84px; }
        }
        @media (max-width: 460px) {
          .site-hdr-cta .full { display: none; }
          .site-hdr-cta .short { display: inline; }
          .site-hdr-cta { padding: 9px 16px; }
          .site-hdr-signin { font-size: 14px; }
        }
      `}</style>
    </header>
  );
}
